import React, { useEffect, useState } from 'react';
import { getRiHealth } from '../../api/auth';

/**
 * R15: small amber banner above the Route Intelligence header that surfaces
 * when more than 5% of guests fail to route, or more than 50 routes are stuck
 * pending. Polls the /route-intelligence/health endpoint once per minute.
 *
 * Returns null on small clients (total <= 10) so a single bad row doesn't
 * read as a 100% failure rate — the backend already enforces that guard.
 */
export default function HealthBanner({ clientId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!clientId) { setData(null); return undefined; }
    let cancelled = false;
    const load = () => {
      getRiHealth(clientId)
        .then((r) => { if (!cancelled) setData(r?.data || null); })
        .catch(() => { if (!cancelled) setData(null); });
    };
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [clientId]);

  if (!data || !data.needs_attention) return null;

  const unreachable = data.counts?.unreachable ?? 0;
  const pending = data.counts?.pending ?? 0;
  const pct = data.unreachable_pct ?? 0;

  return (
    <div
      role="alert"
      className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-700 px-4 py-2.5 text-sm text-amber-900 dark:text-amber-100 flex items-start gap-3"
    >
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 text-xs font-bold flex-shrink-0 mt-0.5">
        !
      </span>
      <div className="flex-1">
        <div className="font-semibold">Routing pipeline needs attention</div>
        <div className="text-xs mt-0.5 text-amber-800 dark:text-amber-200">
          {unreachable > 0 && (
            <>
              {unreachable.toLocaleString()} guest{unreachable === 1 ? '' : 's'} could not be routed
              {' '}({pct}% of total).{' '}
            </>
          )}
          {pending > 50 && (
            <>
              {pending.toLocaleString()} routes are still pending — geocoding may be backed up.
            </>
          )}
        </div>
      </div>
    </div>
  );
}
